<template>
  <div class="orcamento-expansion">
    <div class="orcamento-expansion__intro">
      <div class="orcamento-expansion__identity">
        <div class="orcamento-expansion__identity-icon" aria-hidden="true">
          <v-icon color="white" size="22">mdi-file-document-outline</v-icon>
        </div>
        <div>
          <div class="orcamento-expansion__eyebrow">
            NSR {{ recurso.nsr || recurso.id }}
          </div>
          <div class="orcamento-expansion__title">
            Detalhes do orçamento de despesa
          </div>
          <div class="orcamento-expansion__subtitle">
            Compare o valor planejado com as despesas efetivamente realizadas.
          </div>
        </div>
      </div>

      <div class="orcamento-expansion__context">
        <div class="orcamento-context-item">
          <span>Solicitante</span>
          <strong>{{ nomeSolicitante }}</strong>
        </div>
        <div class="orcamento-context-item">
          <span>Status atual</span>
          <v-chip
            small
            label
            :color="statusVisual.color"
            text-color="white"
            class="orcamento-context-status"
          >
            <v-icon left x-small>{{ statusVisual.icon }}</v-icon>
            {{ $t(recurso.statusPedido) }}
          </v-chip>
        </div>
      </div>
    </div>

    <section
      class="orcamento-expansion-section orcamento-expansion-section--requested"
      aria-labelledby="titulo-solicitado"
    >
      <header class="orcamento-section-header">
        <div class="orcamento-section-heading">
          <div class="orcamento-section-step" aria-hidden="true">1</div>
          <div>
            <div class="orcamento-section-eyebrow">Planejamento</div>
            <h3 id="titulo-solicitado" class="orcamento-section-title">
              O que foi solicitado
            </h3>
            <p class="orcamento-section-description">
              Dados e valores previstos no pedido original.
            </p>
          </div>
        </div>
        <div class="orcamento-section-total">
          <span>Valor solicitado</span>
          <strong>{{ recurso.valorSolicitado | formatoMonetario }}</strong>
        </div>
      </header>

      <div class="orcamento-section-body">
        <div class="orcamento-request-summary">
          <div class="orcamento-info-item">
            <v-icon color="#2563b8" size="20"
              >mdi-office-building-outline</v-icon
            >
            <div>
              <span>Centro de custo</span>
              <strong>{{ descricaoCentroCusto }}</strong>
            </div>
          </div>
          <div class="orcamento-info-item">
            <v-icon color="#2563b8" size="20">mdi-calendar-range</v-icon>
            <div>
              <span>Período previsto</span>
              <strong>
                {{ recurso.periodoInicio | dataParaLeitura }} a
                {{ recurso.periodoEncerramento | dataParaLeitura }}
              </strong>
            </div>
          </div>
          <div class="orcamento-info-item orcamento-info-item--wide">
            <v-icon color="#2563b8" size="20">mdi-text-box-outline</v-icon>
            <div>
              <span>Descrição da solicitação</span>
              <strong>{{ recurso.descricao || "Não informada" }}</strong>
            </div>
          </div>
          <div
            v-if="recurso.observacao"
            class="orcamento-info-item orcamento-info-item--wide"
          >
            <v-icon color="#2563b8" size="20">mdi-message-text-outline</v-icon>
            <div>
              <span>Observação</span>
              <strong>{{ recurso.observacao }}</strong>
            </div>
          </div>
        </div>

        <div
          v-if="possuiMovimentacoes"
          class="orcamento-history"
          aria-label="Andamento do pedido"
        >
          <div class="orcamento-history__title">
            <v-icon size="18" color="#67748e"
              >mdi-timeline-clock-outline</v-icon
            >
            <span>Andamento do pedido</span>
          </div>
          <div class="orcamento-history__items">
            <div v-if="recurso.dataAprovacao" class="orcamento-history-item">
              <span>Aprovação</span>
              <strong v-if="recurso.responsavelAprovacao">
                {{ recurso.responsavelAprovacao.nome }}
              </strong>
              <small>
                {{ recurso.dataAprovacao | dataHoraParaLeitura }}
                {{ $t("Horas") }}
              </small>
            </div>
            <div
              v-if="recurso.dataProcessamento"
              class="orcamento-history-item"
            >
              <span>Início do processamento</span>
              <small>
                {{ recurso.dataProcessamento | dataHoraParaLeitura }}
                {{ $t("Horas") }}
              </small>
            </div>
            <div v-if="recurso.dataEncerramento" class="orcamento-history-item">
              <span>Encerramento</span>
              <small>
                {{ recurso.dataEncerramento | dataHoraParaLeitura }}
                {{ $t("Horas") }}
              </small>
            </div>
            <div v-if="recurso.dataConclusao" class="orcamento-history-item">
              <span>Conclusão</span>
              <strong v-if="recurso.responsavelConclusao">
                {{ recurso.responsavelConclusao.nome }}
              </strong>
              <small>
                {{ recurso.dataConclusao | dataHoraParaLeitura }}
                {{ $t("Horas") }}
              </small>
            </div>
          </div>
        </div>

        <div class="orcamento-table-heading">
          <div>
            <h4>Distribuição solicitada por conta financeira</h4>
            <p>{{ quantidadeItensSolicitados }} conta(s) informada(s).</p>
          </div>
          <v-btn
            v-if="recurso.statusPedido === 'PENDENTE'"
            :disabled="!buttonSave"
            :loading="localLoading"
            class="
              ma-1
              font-weight-bold
              text-none
              btn-primary
              bg-gradient-primary
              px-6
              ms-3
            "
            height="39"
            @click="update"
          >
            <v-icon left>mdi-content-save-outline</v-icon>
            Salvar alterações
          </v-btn>
        </div>

        <v-dialog
          v-if="recurso.statusPedido === 'PENDENTE'"
          v-model="dialog"
          max-width="500px"
        >
          <v-card>
            <v-card-title>
              <span class="text-h5">{{ formTitle }}</span>
            </v-card-title>
            <v-card-text>
              <v-container>
                <v-row>
                  <v-col cols="12">
                    <v-text-field-money
                      v-model="editedItem.valor"
                      v-bind:label="$t('Valor')"
                      v-bind:properties="{
                        prefix: 'R$',
                        readonly: false,
                        disabled: false,
                        outlined: false,
                        clearable: true,
                      }"
                      v-bind:options="{
                        locale: 'pt-BR',
                        length: 12,
                        precision: 2,
                        empty: '0',
                      }"
                      class="font-size-input text-light-input placeholder-light"
                      dense
                      flat
                      filled
                      solo
                    />
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
            <v-card-actions class="px-6 pb-5">
              <v-spacer />
              <v-btn
                class="
                  ma-1
                  font-weight-bold
                  text-capitalize
                  btn-ls btn-secondary
                  bg-gradient-light
                  px-6
                "
                height="39"
                @click="close"
              >
                {{ $t("Cancelar") }}
              </v-btn>
              <v-btn
                class="
                  ma-1
                  font-weight-bold
                  text-none
                  btn-primary
                  bg-gradient-primary
                  px-6
                  ms-3
                "
                height="39"
                @click="save"
              >
                <v-icon left>mdi-content-save-outline</v-icon>
                Confirmar alteração
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="dialogDelete" max-width="500px">
          <v-card>
            <v-card-title class="text-h5">
              {{ $t("Confirma_exclusao?") }}
            </v-card-title>
            <v-card-text>
              O item será removido da solicitação. Para concluir, salve as
              alterações do pedido.
            </v-card-text>
            <v-card-actions class="px-6 pb-5">
              <v-spacer />
              <v-btn
                class="
                  ma-1
                  font-weight-bold
                  text-capitalize
                  btn-ls btn-secondary
                  bg-gradient-light
                  px-6
                "
                height="39"
                @click="closeDelete"
              >
                {{ $t("Cancelar") }}
              </v-btn>
              <v-btn
                color="error"
                dark
                class="ma-1 text-none font-weight-bold px-6 ms-3"
                height="39"
                @click="deleteItemConfirm"
              >
                <v-icon left>mdi-delete-outline</v-icon>
                Remover item
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-data-table
          hide-default-footer
          disable-pagination
          :headers="headersSolicitacao"
          :items="recurso.itemsPedido || []"
          sort-by="contaFinanceira.descricao"
          class="orcamento-detail-table"
        >
          <template
            v-if="recurso.statusPedido === 'PENDENTE'"
            v-slot:item.actions="{ item }"
          >
            <div class="orcamento-table-actions">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    v-bind="attrs"
                    v-on="on"
                    small
                    color="#67748e"
                    class="btn-ls me-0.90 my-1 rounded-sm ma-1"
                    :aria-label="$t('Editar')"
                    @click="editItem(item)"
                  >
                    mdi-pencil-outline
                  </v-icon>
                </template>
                <span>{{ $t("Editar") }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    v-bind="attrs"
                    v-on="on"
                    small
                    color="#67748e"
                    class="btn-ls me-0.90 my-1 rounded-sm ma-1"
                    :aria-label="$t('Excluir')"
                    @click="deleteItem(item)"
                  >
                    mdi-delete-outline
                  </v-icon>
                </template>
                <span>{{ $t("Excluir") }}</span>
              </v-tooltip>
            </div>
          </template>
          <template v-slot:no-data>
            <div class="orcamento-empty-state">
              <v-icon size="28" color="#98a2b3">mdi-inbox-outline</v-icon>
              <span>Nenhuma conta informada na solicitação.</span>
            </div>
          </template>
          <template v-slot:[`item.valor`]="{ item }">
            <strong>{{ item.valor | formatoMonetario }}</strong>
          </template>
        </v-data-table>
      </div>
    </section>

    <div class="orcamento-expansion__transition" aria-hidden="true">
      <span></span>
      <div>
        <v-icon size="18" color="#0f766e">mdi-arrow-down</v-icon>
      </div>
      <span></span>
    </div>

    <card-apuracoes-detalhes-orcamento-despesa
      :item="recurso"
      @atualizado="$emit('atualizado')"
    />
  </div>
</template>

<script>
import DataService from "@/services/ComumDataService";
import CardApuracoesDetalhesOrcamentoDespesa from "./CardApuracoesDetalhesOrcamentoDespesa.vue";
export default {
  props: {
    recurso: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
  },
  components: {
    CardApuracoesDetalhesOrcamentoDespesa,
  },

  data: () => ({
    dialog: false,
    dialogDelete: false,
    buttonSave: false,
    localLoading: false,
    editedIndex: -1,
    editedItem: {},
    defaultItem: {
      valor: 0,
      contaFinanceira: null,
    },
  }),

  computed: {
    headersSolicitacao() {
      const headers = [
        {
          text: "Conta financeira",
          align: "start",
          sortable: true,
          value: "contaFinanceira.descricao",
        },
        {
          text: "Valor solicitado",
          align: "end",
          value: "valor",
        },
      ];
      if (this.recurso.statusPedido === "PENDENTE") {
        headers.push({
          text: "Ações",
          align: "center",
          value: "actions",
          sortable: false,
          width: "112px",
        });
      }
      return headers;
    },

    formTitle() {
      const contaFinanceira =
        this.editedIndex && this.editedIndex.contaFinanceira
          ? this.editedIndex.contaFinanceira.descricao
          : "valor solicitado";
      return `Editar ${contaFinanceira}`;
    },

    nomeSolicitante() {
      return this.recurso.solicitante && this.recurso.solicitante.nome
        ? this.recurso.solicitante.nome
        : "Não informado";
    },

    descricaoCentroCusto() {
      return this.recurso.centroCusto && this.recurso.centroCusto.descricao
        ? this.recurso.centroCusto.descricao
        : "Não informado";
    },

    quantidadeItensSolicitados() {
      return Array.isArray(this.recurso.itemsPedido)
        ? this.recurso.itemsPedido.length
        : 0;
    },

    possuiMovimentacoes() {
      return Boolean(
        this.recurso.dataAprovacao ||
          this.recurso.dataProcessamento ||
          this.recurso.dataEncerramento ||
          this.recurso.dataConclusao
      );
    },

    statusVisual() {
      const status = {
        PENDENTE: { color: "#B45309", icon: "mdi-clock-outline" },
        APROVADO: {
          color: "#15803D",
          icon: "mdi-check-circle-outline",
        },
        PROCESSAMENTO: { color: "#2563EB", icon: "mdi-progress-clock" },
        TRANSFERENCIARECURSO: {
          color: "#7C3AED",
          icon: "mdi-swap-horizontal",
        },
        ENCERRADO: { color: "#4338CA", icon: "mdi-lock-outline" },
        CONCLUIDO: { color: "#0F766E", icon: "mdi-check-all" },
        CANCELADO: {
          color: "#B91C1C",
          icon: "mdi-close-circle-outline",
        },
      };
      return (
        status[this.recurso.statusPedido] || {
          color: "#64748B",
          icon: "mdi-help-circle-outline",
        }
      );
    },
  },

  watch: {
    dialog(val) {
      val || this.close();
    },
    dialogDelete(val) {
      val || this.closeDelete();
    },
  },

  created() {
    this.initialize();
  },

  methods: {
    initialize() {},

    editItem(item) {
      this.editedIndex = item;
      this.editedItem = Object.assign({}, item);
      this.dialog = true;
    },

    deleteItem(item) {
      this.editedIndex = item;
      this.editedItem = Object.assign({}, item);
      this.dialogDelete = true;
    },

    deleteItemConfirm() {
      var itemExistente = this.recurso.itemsPedido.find(
        (item) => item.contaFinanceira.id == this.editedItem.contaFinanceira.id
      );
      // this.recurso.itemsPedido = [];

      if (itemExistente) {
        this.recurso.itemsPedido = this.recurso.itemsPedido.filter((item) => {
          return item.contaFinanceira.id !== itemExistente.contaFinanceira.id;
        });
      }
      this.buttonSave = true;
      this.closeDelete();
    },

    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },

    closeDelete() {
      this.dialogDelete = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },

    save() {
      var itemExistente = this.recurso.itemsPedido.find(
        (item) => item.contaFinanceira.id == this.editedItem.contaFinanceira.id
      );
      // this.recurso.itemsPedido = [];

      if (itemExistente) {
        this.recurso.itemsPedido = this.recurso.itemsPedido.filter((item) => {
          return item.contaFinanceira.id !== itemExistente.contaFinanceira.id;
        });
      }
      this.recurso.itemsPedido.push(this.editedItem);
      console.log("edited 1", this.recurso);
      // } else {
      //   this.recurso.itemspedido.push(this.editedItem);
      //   console.log("edited 2", this.recurso);
      // }
      this.buttonSave = true;
      this.close();
    },

    update() {
      this.localLoading = true;
      DataService.put(
        "pedidoOrcamentoDespesas/" + this.recurso.id,
        this.recurso
      )
        .then(() => {
          this.localLoading = false;
          this.buttonSave = false;
          this.$emit("atualizado");
        })
        .catch((error) => {
          this.localLoading = false;

          window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
          this.erros = error.response.data.errors;
          this.$swal.fire(this.$t("Erro"), this.$t("Tente_novamente"), "error");
        });
    },
  },
};
</script>

<style scoped>
.orcamento-expansion {
  padding: 18px;
  background: #f8fafc;
}

.orcamento-expansion__intro {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 4px 4px 18px;
}

.orcamento-expansion__identity {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 12px;
}

.orcamento-expansion__identity-icon {
  display: inline-flex;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #344767;
  box-shadow: 0 5px 12px rgba(52, 71, 103, 0.18);
}

.orcamento-expansion__eyebrow,
.orcamento-section-eyebrow {
  color: #667085;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  line-height: 1.4;
  text-transform: uppercase;
}

.orcamento-expansion__title {
  color: #344767;
  font-size: 1.05rem;
  font-weight: 800;
  line-height: 1.4;
}

.orcamento-expansion__subtitle {
  margin-top: 2px;
  color: #67748e;
  font-size: 0.875rem;
  line-height: 1.45;
}

.orcamento-expansion__context {
  display: flex;
  align-items: center;
  gap: 12px;
}

.orcamento-context-item {
  display: flex;
  min-width: 150px;
  min-height: 54px;
  flex-direction: column;
  justify-content: center;
  padding: 8px 12px;
  border: 1px solid #e4e7ec;
  border-radius: 9px;
  background: #fff;
}

.orcamento-context-item > span,
.orcamento-section-total span,
.orcamento-info-item span,
.orcamento-history-item span {
  color: #667085;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.4;
}

.orcamento-context-item strong {
  overflow: hidden;
  color: #344767;
  font-size: 0.875rem;
  line-height: 1.45;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.orcamento-context-status {
  align-self: flex-start;
  margin-top: 2px;
  font-weight: 700;
}

.orcamento-expansion-section {
  overflow: hidden;
  border: 1px solid #dde5ef;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 4px 16px rgba(52, 71, 103, 0.07);
}

.orcamento-expansion-section--requested {
  border-top: 4px solid #2563b8;
}

.orcamento-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 18px 20px;
  border-bottom: 1px solid #e7edf5;
  background: linear-gradient(90deg, #f2f7fd 0%, #fff 68%);
}

.orcamento-section-heading {
  display: flex;
  align-items: center;
  gap: 12px;
}

.orcamento-section-step {
  display: inline-flex;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #2563b8;
  color: #fff;
  font-size: 1rem;
  font-weight: 800;
}

.orcamento-section-title {
  margin: 0;
  color: #344767;
  font-size: 1.08rem;
  font-weight: 800;
  line-height: 1.35;
}

.orcamento-section-description {
  margin: 2px 0 0;
  color: #67748e;
  font-size: 0.875rem;
  line-height: 1.4;
}

.orcamento-section-total {
  min-width: 170px;
  padding-left: 20px;
  border-left: 1px solid #dbe6f2;
  text-align: right;
}

.orcamento-section-total span,
.orcamento-section-total strong {
  display: block;
}

.orcamento-section-total strong {
  color: #1d4f91;
  font-size: 1.25rem;
  font-weight: 800;
  line-height: 1.35;
}

.orcamento-section-body {
  padding: 20px;
}

.orcamento-request-summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 18px;
}

.orcamento-info-item {
  display: flex;
  min-height: 68px;
  align-items: flex-start;
  gap: 10px;
  padding: 13px 14px;
  border: 1px solid #e7edf5;
  border-radius: 9px;
  background: #fbfdff;
}

.orcamento-info-item > div {
  min-width: 0;
}

.orcamento-info-item span,
.orcamento-info-item strong {
  display: block;
}

.orcamento-info-item strong {
  margin-top: 2px;
  color: #344767;
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.orcamento-info-item--wide {
  grid-column: span 2;
}

.orcamento-history {
  margin-bottom: 20px;
  padding: 14px;
  border: 1px solid #e4e7ec;
  border-radius: 9px;
  background: #f9fafb;
}

.orcamento-history__title {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 10px;
  color: #475467;
  font-size: 0.875rem;
  font-weight: 800;
}

.orcamento-history__items {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}

.orcamento-history-item {
  position: relative;
  min-width: 0;
  padding: 2px 12px 2px 14px;
  border-left: 3px solid #cbd5e1;
}

.orcamento-history-item span,
.orcamento-history-item strong,
.orcamento-history-item small {
  display: block;
}

.orcamento-history-item strong {
  overflow: hidden;
  margin-top: 2px;
  color: #344767;
  font-size: 0.8125rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.orcamento-history-item small {
  margin-top: 2px;
  color: #667085;
  font-size: 0.75rem;
  line-height: 1.4;
}

.orcamento-table-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 10px;
}

.orcamento-table-heading h4 {
  margin: 0;
  color: #344767;
  font-size: 0.95rem;
  font-weight: 800;
}

.orcamento-table-heading p {
  margin: 2px 0 0;
  color: #67748e;
  font-size: 0.8125rem;
}

.orcamento-detail-table {
  overflow: hidden;
  border: 1px solid #e4e7ec;
  border-radius: 9px;
}

.orcamento-table-actions {
  display: inline-flex;
  align-items: center;
}

.orcamento-empty-state {
  display: flex;
  min-height: 92px;
  align-items: center;
  justify-content: center;
  gap: 9px;
  color: #667085;
  font-size: 0.875rem;
}

.orcamento-expansion__transition {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 12px;
  padding: 10px 24px;
}

.orcamento-expansion__transition span {
  height: 1px;
  background: #dce7e4;
}

.orcamento-expansion__transition div {
  display: inline-flex;
  width: 30px;
  height: 30px;
  align-items: center;
  justify-content: center;
  border: 1px solid #cfe3de;
  border-radius: 50%;
  background: #ecf8f5;
}

@media (max-width: 959px) {
  .orcamento-expansion__intro,
  .orcamento-section-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .orcamento-expansion__context {
    width: 100%;
  }

  .orcamento-context-item {
    min-width: 0;
    flex: 1 1 0;
  }

  .orcamento-section-total {
    width: 100%;
    padding-top: 12px;
    padding-left: 0;
    border-top: 1px solid #dbe6f2;
    border-left: 0;
    text-align: left;
  }

  .orcamento-history__items {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 599px) {
  .orcamento-expansion {
    padding: 12px;
  }

  .orcamento-expansion__context,
  .orcamento-table-heading {
    align-items: stretch;
    flex-direction: column;
  }

  .orcamento-request-summary,
  .orcamento-history__items {
    grid-template-columns: 1fr;
  }

  .orcamento-info-item--wide {
    grid-column: auto;
  }

  .orcamento-section-header,
  .orcamento-section-body {
    padding: 16px;
  }
}
</style>
